// Giỏ mua thêm sản phẩm in trên cổng khách (kế hoạch D §8.2/§8.5).
//
// Số tiền hiện ở đây là TẠM TÍNH. Số thật là `payable` máy chủ trả về sau khi
// đặt, và mã QR phát đúng bằng số đó — màn hình không bao giờ gửi giá lên.
//
// Màn hình cố ý chặn đúng những giỏ máy chủ sẽ từ chối (số lượng vượt trần,
// món "mua 1 lần" bị tăng số lượng, cỡ không có giá): để khách bấm đặt rồi
// mới ăn câu lỗi kỹ thuật là mất khách ngay ở bước ra tiền.
import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { Button } from "@/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/ui/dialog";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";
import { Textarea } from "@/ui/textarea";
import { formatVND } from "@/lib/format";
import {
  createPortalPrintOrder,
  fetchPortalShop,
  type PortalPrintOrderResponse,
} from "./portal-api";
import { cartTotal, lineTotal, maxQtyOf, sizesOf, unitPrice, type ShopItem } from "./shopPricing";

type Props = {
  orderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Nhận nguyên phiếu máy chủ trả về để mở màn trả tiền. */
  onCreated: (res: PortalPrintOrderResponse) => void;
};

/** Một dòng trong giỏ; mỗi mục hàng chỉ có tối đa một dòng. */
type Line = { itemId: string; size: string; qty: number };

export function PortalShopDialog({ orderId, open, onOpenChange, onCreated }: Props) {
  const shopQ = useQuery({
    queryKey: ["portal-shop", orderId],
    queryFn: ({ signal }) => fetchPortalShop(orderId, { signal }),
    enabled: open,
  });
  const [lines, setLines] = useState<Line[]>([]);
  const [zone, setZone] = useState("");
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Chỉ MỘT lượt đặt bay cùng lúc — hai lượt là hai đơn in, khách trả một
  // phiếu còn phiếu kia treo tới lúc hết hạn.
  const inFlight = useRef(false);

  const shop = shopQ.data;
  const items = useMemo(() => shop?.items ?? [], [shop]);
  const byId = useMemo(() => new Map(items.map((it) => [it.id, it])), [items]);

  const cart = useMemo(() => {
    const rows = lines
      .map((l) => ({ item: byId.get(l.itemId), size: l.size, qty: l.qty }))
      .filter((r): r is { item: ShopItem; size: string; qty: number } => Boolean(r.item));
    const fee = shop?.shipping.find((z) => z.id === zone)?.fee ?? 0;
    return { ...cartTotal(rows, fee, shop?.freeShipFrom ?? 0), fee };
  }, [lines, byId, shop, zone]);

  const add = (item: ShopItem) => {
    setLines((prev) => {
      const found = prev.find((l) => l.itemId === item.id);
      if (!found) return [...prev, { itemId: item.id, size: sizesOf(item)[0] ?? "", qty: 1 }];
      return prev.map((l) =>
        l.itemId === item.id ? { ...l, qty: Math.min(l.qty + 1, maxQtyOf(item)) } : l,
      );
    });
  };

  const setQty = (item: ShopItem, qty: number) => {
    setLines((prev) =>
      qty <= 0
        ? prev.filter((l) => l.itemId !== item.id)
        : prev.map((l) =>
            l.itemId === item.id ? { ...l, qty: Math.min(qty, maxQtyOf(item)) } : l,
          ),
    );
  };

  const setSize = (itemId: string, size: string) => {
    setLines((prev) => prev.map((l) => (l.itemId === itemId ? { ...l, size } : l)));
  };

  const needsAddress = zone !== "" && address.trim() === "";
  const canSubmit = lines.length > 0 && cart.goods > 0 && !needsAddress && !submitting;

  const submit = () => {
    if (!canSubmit || inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setError("");
    createPortalPrintOrder(orderId, {
      items: lines.map((l) => ({ catalogItemId: l.itemId, size: l.size, qty: l.qty })),
      shippingZone: zone,
      address: address.trim(),
      note: note.trim(),
    })
      .then(onCreated)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Không đặt được sản phẩm in");
      })
      .finally(() => {
        inFlight.current = false;
        setSubmitting(false);
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-lg md:max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" /> Mua thêm ảnh in
          </DialogTitle>
          <DialogDescription>
            Chị chọn sản phẩm rồi thanh toán bằng mã QR. Studio in xong sẽ báo chị ngay ạ.
          </DialogDescription>
        </DialogHeader>

        {shopQ.isPending && (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Đang tải danh sách sản phẩm…
          </div>
        )}

        {shop && !shop.canShop && (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {shop.reason ?? "Đơn này chưa mua thêm được ạ."}
          </p>
        )}

        {shop?.canShop && (
          <div className="grid gap-4">
            <div className="grid gap-2">
              {items.map((item) => {
                const line = lines.find((l) => l.itemId === item.id);
                const sizes = sizesOf(item);
                const priced =
                  item.pricing.kind === "by_size" ? sizes.length > 0 : unitPrice(item, "") > 0;
                return (
                  <ItemRow
                    key={item.id}
                    item={item}
                    sizes={sizes}
                    priced={priced}
                    {...(line ? { line } : {})}
                    onAdd={() => add(item)}
                    onQty={(qty) => setQty(item, qty)}
                    onSize={(size) => setSize(item.id, size)}
                  />
                );
              })}
            </div>

            <DeliverySection
              zones={shop.shipping}
              zone={zone}
              onZone={setZone}
              address={address}
              onAddress={setAddress}
              note={note}
              onNote={setNote}
            />

            <CartSummary
              goods={cart.goods}
              shipping={cart.shipping}
              fee={cart.fee}
              total={cart.total}
            />

            {needsAddress && (
              <p className="text-sm text-muted-foreground">
                Chị điền giúp em địa chỉ nhận hàng nhé.
              </p>
            )}
            {error && <p className="text-sm text-danger-text">{error}</p>}

            <Button onClick={submit} disabled={!canSubmit} className="w-full">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Đặt và thanh toán
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Một mục hàng: chưa có trong giỏ thì hiện nút Thêm, có rồi thì hiện bộ đếm. */
function ItemRow({
  item,
  sizes,
  priced,
  line,
  onAdd,
  onQty,
  onSize,
}: {
  item: ShopItem;
  sizes: string[];
  priced: boolean;
  line?: Line;
  onAdd: () => void;
  onQty: (qty: number) => void;
  onSize: (size: string) => void;
}) {
  const qty = line?.qty ?? 0;
  const size = line?.size ?? sizes[0] ?? "";
  const unit = unitPrice(item, size);

  return (
    <div className="grid gap-2 rounded-lg border bg-surface p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{item.name}</span>
        <span className="text-sm text-muted-foreground">
          {priced ? formatVND(unit) : "Chưa có giá"}
        </span>
      </div>

      {sizes.length > 0 && (
        <div className="flex items-center gap-2">
          <Label htmlFor={`size-${item.id}`} className="text-sm">
            Cỡ {item.name}
          </Label>
          <select
            id={`size-${item.id}`}
            className="min-h-11 flex-1 rounded-lg border px-2"
            value={size}
            onChange={(e) => onSize(e.target.value)}
          >
            {sizes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      )}

      {line ? (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            aria-label={`Giảm số lượng ${item.name}`}
            onClick={() => onQty(qty - 1)}
          >
            {qty <= 1 ? <Trash2 className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
          </Button>
          <span className="min-w-8 text-center">{qty}</span>
          <Button
            variant="outline"
            size="icon"
            aria-label={`Tăng số lượng ${item.name}`}
            disabled={qty >= maxQtyOf(item)}
            onClick={() => onQty(qty + 1)}
          >
            <Plus className="h-4 w-4" />
          </Button>
          <span className="ml-auto font-medium">{formatVND(lineTotal(item, size, qty))}</span>
        </div>
      ) : (
        <Button variant="outline" disabled={!priced} onClick={onAdd}>
          <Plus className="mr-1 h-4 w-4" /> Thêm {item.name}
        </Button>
      )}
    </div>
  );
}

/** Chọn vùng giao, địa chỉ và ghi chú — tất cả những gì liên quan tới nhận hàng. */
function DeliverySection({
  zones,
  zone,
  onZone,
  address,
  onAddress,
  note,
  onNote,
}: {
  zones: { id: string; label: string; fee: number }[];
  zone: string;
  onZone: (id: string) => void;
  address: string;
  onAddress: (v: string) => void;
  note: string;
  onNote: (v: string) => void;
}) {
  return (
    <>
      {zones.length > 0 && (
        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium">Nhận hàng</legend>
          {zones.map((z) => (
            <div key={z.id} className="flex items-center gap-2">
              <input
                type="radio"
                id={`zone-${z.id}`}
                name="shipping-zone"
                className="h-5 w-5"
                checked={zone === z.id}
                onChange={() => onZone(z.id)}
              />
              <Label htmlFor={`zone-${z.id}`} className="flex-1">
                {z.label}
              </Label>
              <span className="text-sm text-muted-foreground">{formatVND(z.fee)}</span>
            </div>
          ))}
        </fieldset>
      )}

      {zone !== "" && (
        <div className="grid gap-1.5">
          <Label htmlFor="portal-shop-address">Địa chỉ nhận hàng *</Label>
          <Input
            id="portal-shop-address"
            value={address}
            onChange={(e) => onAddress(e.target.value)}
            placeholder="Số nhà, đường, phường, quận"
          />
        </div>
      )}

      <div className="grid gap-1.5">
        <Label htmlFor="portal-shop-note">Ghi chú (nếu có)</Label>
        <Textarea
          id="portal-shop-note"
          rows={2}
          value={note}
          onChange={(e) => onNote(e.target.value)}
        />
      </div>
    </>
  );
}

/**
 * Bảng tạm tính.
 *
 * Câu "số cuối cùng do studio chốt" KHÔNG phải câu rào trách nhiệm cho vui:
 * máy chủ mới là nơi định giá, và mã QR phát đúng bằng số của máy chủ.
 */
function CartSummary({
  goods,
  shipping,
  fee,
  total,
}: {
  goods: number;
  shipping: number;
  fee: number;
  total: number;
}) {
  return (
    <div className="grid gap-1 rounded-lg border bg-surface p-3 text-sm">
      <div className="flex items-center justify-between">
        <span>Tiền hàng</span>
        <b>{formatVND(goods)}</b>
      </div>
      <div className="flex items-center justify-between">
        <span>Phí giao</span>
        <b>
          {shipping > 0 ? formatVND(shipping) : "0 ₫"}
          {fee > 0 && shipping === 0 ? " · Miễn phí giao" : ""}
        </b>
      </div>
      <div className="flex items-center justify-between border-t pt-1">
        <span>Tạm tính</span>
        <b data-testid="cart-total" className="text-base">
          {formatVND(total)}
        </b>
      </div>
      <p className="text-xs text-muted-foreground">
        Số cuối cùng do studio chốt ngay khi chị bấm đặt, mã QR phát đúng bằng số đó ạ.
      </p>
    </div>
  );
}
